import React, { useState, useEffect } from 'react';
import { promoPackService } from '../../api/promoPackService';
import { productService } from '../../api/productService';
import { uploadService } from '../../api/uploadService';
import '../Common/Common.css';

function PromoPackForm({ promoPack, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    image_url: '',
    description: '',
    promo_price: '',
    fake_original_price: '',
    is_active: true,
  });
  const [items, setItems] = useState([{ product_id: '', quantity: 1 }]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchProducts();
    if (promoPack) {
      setFormData({
        name: promoPack.name || '',
        slug: promoPack.slug || '',
        image_url: promoPack.image_url || '',
        description: promoPack.description || '',
        promo_price: promoPack.promo_price || '',
        fake_original_price: promoPack.fake_original_price || '',
        is_active: promoPack.is_active ?? true,
      });
      if (promoPack.promo_pack_items && promoPack.promo_pack_items.length > 0) {
        setItems(
          promoPack.promo_pack_items.map((item) => ({
            product_id: item.product_id,
            quantity: item.quantity,
          }))
        );
      }
      setImagePreview(promoPack.image_url || '');
    }
  }, [promoPack]);

  const fetchProducts = async () => {
    try {
      const response = await productService.getProducts({ per_page: 1000 });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSlugGenerate = () => {
    const slug = formData.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData({ ...formData, slug });
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Auto-upload the image
      try {
        setUploadingImage(true);
        const response = await uploadService.uploadMedia(file, 'image', 'promo-packs');
        const uploadedUrl = response.url || response.path || response;
        setFormData(prev => ({ ...prev, image_url: uploadedUrl }));
        setImageFile(null);
        alert('Image téléchargée avec succès !');
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Échec du téléchargement de l\'image');
        // Reset preview on error
        setImagePreview(formData.image_url || '');
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const addItem = () => {
    setItems([...items, { product_id: '', quantity: 1 }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) {
      alert('Le pack promo doit avoir au moins un produit');
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.slug) {
      alert('Veuillez remplir le nom et le slug');
      return;
    }

    const validItems = items.filter((item) => item.product_id && item.quantity > 0);
    if (validItems.length === 0) {
      alert('Veuillez ajouter au moins un produit au pack promo');
      return;
    }

    const dataToSend = {
      ...formData,
      items: validItems,
    };

    setLoading(true);
    try {
      if (promoPack) {
        await promoPackService.updatePromoPack(promoPack.id, dataToSend);
      } else {
        await promoPackService.createPromoPack(dataToSend);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving promo pack:', error);
      alert(error.response?.data?.message || 'Échec de l\'enregistrement du pack promo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <div className="form-group">
        <label htmlFor="name">Nom *</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="form-control"
          required
        />
      </div>

      <div className="form-group">
        <label htmlFor="slug">Slug *</label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            className="form-control"
            required
          />
          <button type="button" onClick={handleSlugGenerate} className="btn btn-secondary">
            Générer
          </button>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="image">Image</label>
        <div style={{ marginBottom: '10px' }}>
          <input
            type="file"
            id="image"
            accept="image/*"
            onChange={handleImageSelect}
            className="form-control"
            disabled={uploadingImage}
          />
          {uploadingImage && (
            <div style={{ marginTop: '10px', color: '#007bff' }}>
              Téléchargement en cours...
            </div>
          )}
        </div>

        {imagePreview && (
          <div style={{ marginTop: '10px' }}>
            <img
              src={imagePreview}
              alt="Aperçu"
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '5px' }}
            />
          </div>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="description">Description</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows="3"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="promo_price">Prix promo *</label>
          <input
            type="number"
            id="promo_price"
            name="promo_price"
            value={formData.promo_price}
            onChange={handleChange}
            className="form-control"
            step="0.01"
            min="0"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="fake_original_price">Prix original factice</label>
          <input
            type="number"
            id="fake_original_price"
            name="fake_original_price"
            value={formData.fake_original_price}
            onChange={handleChange}
            className="form-control"
            step="0.01"
            min="0"
          />
          <small>Pour afficher la réduction</small>
        </div>
      </div>

      <div className="form-group">
        <label>Produits dans le pack *</label>
        {items.map((item, index) => (
          <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <select
              value={item.product_id}
              onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
              className="form-control"
              style={{ flex: 2 }}
              required
            >
              <option value="">Sélectionner un produit</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
            <input
              type="number"
              value={item.quantity}
              onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value))}
              className="form-control"
              style={{ flex: 1 }}
              min="1"
              placeholder="Qté"
              required
            />
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="btn btn-danger"
              disabled={items.length === 1}
            >
              Supprimer
            </button>
          </div>
        ))}
        <button type="button" onClick={addItem} className="btn btn-secondary">
          Ajouter un produit
        </button>
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <span>Actif</span>
        </label>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Annuler
        </button>
        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? 'Enregistrement...' : promoPack ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
}

export default PromoPackForm;
